<template>
    <ui-segmented class="chart-period-selector"
        v-bind:class="{ 'chart-period-selector--compact': isCompact }"
        v-bind:value="value"
        v-bind:options="options"
        v-on:input="$emit('input', $event)"/>
</template>

<script>
import UiSegmented from '~/components/UiSegmented.vue';

// Period keys are the range length in days; 0 stands for the whole history.
export const PERIOD_DAY = 1;
export const PERIOD_WEEK = 7;
export const PERIOD_MONTH = 30;
export const PERIOD_QUARTER = 90;
export const PERIOD_HALF_YEAR = 180;
export const PERIOD_YEAR = 365;
export const PERIOD_ALL = 0;

/**
 * Range switcher above a chart: a tray with a sliding pill and worded labels
 * (day, week, month, year, all). On a phone it shrinks to plain 1D/7D/1M codes
 * with the chosen one in a tinted pill, so it fits beside the card title.
 * v-model holds the length in days, 0 for everything.
 */
export default {
    props: {
        value: Number,
        periods: {
            type: Array,
            default: () => [PERIOD_MONTH, PERIOD_QUARTER, PERIOD_YEAR, PERIOD_ALL],
        },
        // Short codes on every screen (the token page has no room for the words);
        // left unset, the phone decides.
        compact: {
            type: Boolean,
            default: undefined,
        },
    },

    computed: {
        isCompact() {
            return this.compact === undefined ? this.isMobile : this.compact;
        },

        options() {
            const labels = this.isCompact ? {
                [PERIOD_DAY]: this.$t('common.period_short.day'),
                [PERIOD_WEEK]: this.$t('common.period_short.week'),
                [PERIOD_MONTH]: this.$t('common.period_short.month'),
                [PERIOD_QUARTER]: this.$t('common.period_short.three_months'),
                [PERIOD_HALF_YEAR]: this.$t('common.period_short.half_year'),
                [PERIOD_YEAR]: this.$t('common.period_short.year'),
                [PERIOD_ALL]: this.$t('common.period_short.all'),
            } : {
                [PERIOD_DAY]: this.$t('common.day'),
                [PERIOD_WEEK]: this.$t('common.week'),
                [PERIOD_MONTH]: this.$t('common.month'),
                [PERIOD_QUARTER]: this.$t('common.three_months'),
                [PERIOD_HALF_YEAR]: this.$t('common.half_year'),
                [PERIOD_YEAR]: this.$t('common.year'),
                [PERIOD_ALL]: this.$t('common.all'),
            };

            return this.periods.map(value => ({ value, label: labels[value] || String(value) }));
        },
    },

    components: {
        UiSegmented,
    },
};
</script>

<style lang="scss">
.chart-period-selector {
    flex-shrink: 0;

    .ui-segmented__option {
        height: 28px;
        padding: 0 11px;
        font-size: 12.5px;
    }

    .ui-segmented__thumb {
        height: 28px;
    }

    // Phone: no tray, just the codes in a row, the chosen one on a soft pill
    // that slides between them.
    &--compact {
        gap: 0;
        padding: 0;
        background: transparent;
        border-radius: 0;

        .ui-segmented__option {
            padding: 0 8px;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0;
            font-variant-numeric: tabular-nums;
        }

        .ui-segmented__thumb {
            top: 0;
            background: var(--body-light-muted-color);
            box-shadow: none;
        }
    }
}
</style>
